import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, ChevronDown, ChevronRight, Mail, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { EmailBodyEditor } from "@/components/email/EmailBodyEditor";
import { isBlankHtml, textToHtml } from "@/components/email/htmlCodec";

const PROSE = "text-sm text-slate-600 leading-relaxed [&_p]:my-2 [&_p:first-child]:mt-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_a]:text-brand [&_a]:underline [&_h2]:font-semibold [&_h2]:text-base [&_h3]:font-semibold [&_table]:border-collapse [&_table]:my-2 [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-slate-200 [&_th]:px-2 [&_th]:py-1 [&_img]:inline-block [&_img]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:bg-slate-50 [&_pre]:p-2 [&_pre]:rounded break-words";

// Formatted (server-sanitized) HTML when present; historical plain text shown exactly as stored.
export function ActivityDescription({ html, text, testId }) {
  if (html) return <div className={PROSE} data-testid={testId} dangerouslySetInnerHTML={{ __html: html }} />;
  if (!text) return null;
  return <p className="text-sm text-slate-600 whitespace-pre-line break-words" data-testid={testId}>{text}</p>;
}

export function ActivityDescriptionEditor({ value, onChange, testId = "activity-desc" }) {
  return <EmailBodyEditor testId={testId} value={value} onChange={onChange} showFields={false} cleanPaste compact
    modes={[["design", "Visual"], ["html", "HTML"]]} />;
}

export const descriptionPayload = (html) => (isBlankHtml(html) ? { description: "", description_html: null } : { description_html: html });

function EmailView({ a }) {
  const [d, setD] = useState(null);
  React.useEffect(() => {
    api.get(`/activities/${a.id}/email`).then((r) => setD(r.data)).catch((e) => setD({ error: e?.response?.status === 404 ? "You don't have access to this email." : apiError(e) }));
  }, [a.id]);
  if (!d) return <p className="text-xs text-slate-400 mt-2">Loading email…</p>;
  if (d.error) return <p className="text-xs text-rose-600 mt-2" data-testid={`activity-email-error-${a.id}`}>{d.error}</p>;
  const row = (l, v) => v ? <p className="text-xs text-slate-600"><span className="inline-block w-14 text-slate-400">{l}</span>{v}</p> : null;
  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid={`activity-email-${a.id}`}>
      <p className="text-sm font-semibold text-slate-800 mb-1">{d.subject || "(no subject)"}</p>
      {row("From", [d.from_name, d.from_email && `<${d.from_email}>`].filter(Boolean).join(" "))}
      {row("To", (d.to || []).join(", "))}
      {row("CC", (d.cc || []).join(", "))}
      {row("Sent", d.sent_at ? new Date(d.sent_at).toLocaleString() : "")}
      <div className="mt-2 rounded bg-white border border-slate-100 p-3 overflow-x-auto">
        {d.html ? <div className={PROSE} data-testid={`activity-email-body-${a.id}`} dangerouslySetInnerHTML={{ __html: d.html }} />
          : <p className="text-sm text-slate-500 italic" data-testid={`activity-email-nobody-${a.id}`}>Full email content not available</p>}
      </div>
      {(d.attachments || []).length > 0 && <div className="mt-2 flex flex-wrap gap-2" data-testid={`activity-email-atts-${a.id}`}>
        {d.attachments.map((x) => x.downloadable && d.communication_id
          ? <a key={x.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/communications/${d.communication_id}/attachments/${x.id}`} onClick={async (e) => { e.preventDefault(); try { const r = await api.get(`/communications/${d.communication_id}/attachments/${x.id}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const l = document.createElement("a"); l.href = u; l.download = x.name || "attachment"; l.click(); URL.revokeObjectURL(u); } catch (er) { toast.error(apiError(er)); } }} className="inline-flex items-center gap-1 text-xs text-brand hover:underline"><Paperclip className="w-3 h-3" />{x.name}</a>
          : <span key={x.id || x.name} className="inline-flex items-center gap-1 text-xs text-slate-500"><Paperclip className="w-3 h-3" />{x.name}</span>)}
      </div>}
    </div>
  );
}

export function ActivityItem({ a, onSaved, children }) {
  const { user, isAdmin, isSuper } = useAuth();
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const canEdit = a.user_id === user?.id || isAdmin || isSuper;
  const isEmail = a.type === "Email";
  const save = async () => {
    try { await api.patch(`/activities/${a.id}`, descriptionPayload(edit)); setEdit(null); toast.success("Activity updated"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const Chev = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-md border border-slate-200 bg-white mb-1" data-testid={`activity-${a.id}`}>
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === "Enter") setOpen((o) => !o); }}
        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50" data-testid={`activity-row-${a.id}`} aria-expanded={open}>
        <Chev className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-xs font-semibold text-slate-700 shrink-0 inline-flex items-center gap-1">{isEmail && <Mail className="w-3 h-3 text-brand" />}{a.type}</span>
        <span className="text-sm text-slate-600 truncate flex-1 min-w-0">{a.subject || (a.description || "").replace(/<[^>]+>/g, " ").slice(0, 120)}</span>
        <span className="text-[11px] text-slate-400 shrink-0">{a.activity_at ? new Date(a.activity_at).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
        {canEdit && edit === null && open && <button type="button" onClick={(e) => { e.stopPropagation(); setEdit(a.description_html || textToHtml(a.description || "")); }} title="Edit description"
          className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`activity-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
      </div>
      {open && <div className="px-3 pb-3 pt-1 border-t border-slate-100" data-testid={`activity-expanded-${a.id}`}>
        {edit !== null ? (
          <div className="mt-2 space-y-2">
            <ActivityDescriptionEditor value={edit} onChange={setEdit} testId={`activity-edit-desc-${a.id}`} />
            <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={save} data-testid={`activity-edit-save-${a.id}`}>Save</Button></div>
          </div>
        ) : !isEmail && <div className="mt-1"><ActivityDescription html={a.description_html} text={a.description} testId={`activity-desc-view-${a.id}`} /></div>}
        {isEmail && edit === null && <EmailView a={a} />}
        {a.user_name && <p className="text-[11px] text-slate-400 mt-1">Logged by {a.user_name}</p>}
        {children}
      </div>}
    </div>
  );
}
